import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row, Tooltip, Select, Spin } from 'antd';
import { Box } from '@mui/material';
import { debounce, isEmpty, trim } from 'lodash';

import Modal from "../../components/uiComponent/modal/index";
import Input from '../../components/uiComponent/inputs/labelInput/index.js';
import Button from "../../components/uiComponent/button/index";
import AsyncSearch from '../../components/uiComponent/asyncSelect/index.js';

import ProductImg from "../../assets/images/newImages/nestle.svg";
import copyIcon from "../../assets/images/newImages/copy-white-icon.svg";
import deleteIcon from "../../assets/images/newImages/delete-outlined.svg";
import noData from "../../assets/images/newImages/no-data-icon.svg";

import { copyToClip } from '../../utils/utils';
import InventoryWrapper from './style.js';
import { GetProductsList, MapProducts, clearField } from '../../slices/3-layer-products.js';

const MapProductModal = ({ open, onHide, onSave, rowIndex, handleShowMappingAlert }) => {
  const dispatch = useDispatch();

  const [addedProducts, setAddedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [page, setPage] = useState({ pageSize: 50, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '' });

  const {
    skus,
    productsList,
    mappingProducts,
    fetchingProductsList,
    totalProductsListCount
  } = useSelector((state) => state.threeLayerProducts || {});

  useEffect(() => {
    dispatch(GetProductsList({
      sku_id: skus[rowIndex].id,
      page,
      filters
    }));

    return () => {
      dispatch(clearField({
        field: 'productsList'
      }));
    }
  }, []);

  const getMappedProductsList = (productsList) => {
    return productsList?.map(({ id: value, image_url: image, name: label, identifier_type, identifier_value }) => ({
      value,
      image,
      label,
      identifier1: identifier_value,
      identifier_type
    })) || [];
  };

  const handleAddProduct = () => {
    if (selectedProduct && quantity) {
      const newProduct = {
        id: selectedProduct.value,
        image: selectedProduct.image || ProductImg,
        title: selectedProduct.label,
        identifier_type: selectedProduct.identifier_type,
        identifier_value: selectedProduct.identifier1,
        qty: quantity
      };

      setAddedProducts(prev => {
        const existingItemIndex = prev.findIndex(item => item.identifier_value === newProduct.identifier_value);

        if (existingItemIndex !== -1) {
          const updatedProducts = [...prev];
          updatedProducts[existingItemIndex].qty = Number(updatedProducts[existingItemIndex].qty) + Number(newProduct.qty);
          return updatedProducts;
        }
        return [newProduct, ...prev];
      });
      setSelectedProduct(null);
      setQuantity("");
      setFilters({ search: '' });
      dispatch(GetProductsList({
        sku_id: skus[rowIndex].id,
        page
      }));
    }
  };

  const handleDeleteSku = (index) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLoadMoreProducts = () => {
    if (
      !fetchingProductsList &&
      totalProductsListCount !== productsList.length
    ) {
      const newPage = { ...page, pageNumber: page.pageNumber + 1 };
      setPage(newPage);

      dispatch(GetProductsList({
        sku_id: skus[rowIndex].id,
        page: newPage,
        filters,
        saveData: true
      }));
    }
  };

  const handleProductSearch = debounce((value, callback) => {
    if (trim(value) || filters.search) {
      setFilters({ search: trim(value) });
      setPage(page => ({ ...page, pageNumber: 1 }));

      dispatch(GetProductsList({
        sku_id: skus[rowIndex].id,
        page: { ...page, pageNumber: 1 },
        filters: { search: trim(value) },
      }))
        .then(({ payload }) => {
          const { data } = payload?.data || {};
          callback?.(getMappedProductsList(data));
        });
    }
  }, 800);

  const isAllowedMapProduct = skus[rowIndex]?.is_variety_pack ? true : addedProducts?.length === 0 ? true : false

  return (
    <Modal
      open={open}
      onHide={onHide}
      size="lg"
      closeText="Cancel"
      saveText="Confirm"
      header={true}
      closeButton={true}
      onClose={onHide}
      onSave={() => {
        const mappedProducts = addedProducts.map(({ id, qty }) => ({ id, qty }));
        dispatch(MapProducts({
          mappedProducts,
          sku_id: skus[rowIndex].id
        })).then(({ payload }) => {
          if (payload?.success) {
            onSave();
            onHide();
          }
          else if (!isEmpty(payload?.errorData)) handleShowMappingAlert();
        });
      }}
      buttonDisabled={mappingProducts || !addedProducts?.length}
    >
      <InventoryWrapper className='sku-modal'>
        <Spin tip='Loading...' spinning={mappingProducts}>
          <h4 className='text-primary fw-bold mb-3'>MAP Product to SKU</h4>
          <div className="product-title mb-4">
            <img src={skus[rowIndex].image_url || ProductImg} alt="product" width={28} height={28} className='product-img' />
            <div className='w-100'>
              <Tooltip placement="bottom" title={skus[rowIndex].item_name}>
                <span className="product-name">{skus[rowIndex].item_name}</span>
              </Tooltip>
              <div className='d-flex align-items-center gap-2'>
                <div className='asin'>
                  <span>ASIN <span>{skus[rowIndex].vendorasin?.asin}</span></span>
                  <img src={copyIcon} alt='copy' className='cursor-pointer'
                    onClick={() => copyToClip(skus[rowIndex].vendorasin?.asin)}
                  />
                </div>
                <div className='asin sku'>
                  <span>SKU <span>{skus[rowIndex].sku}</span></span>
                  <img src={copyIcon} alt='copy' className='cursor-pointer'
                    onClick={() => copyToClip(skus[rowIndex].sku)}
                  />
                </div>
              </div>
            </div>
          </div>
          <Row gutter={24} className='mb-5'>
            <Col span={11}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label
                  style={{
                    marginBottom: 4,
                    color: '#78909C',
                    fontSize: 12
                  }}
                >
                  Search Products
                </label>
                <AsyncSearch
                  placeholder="Product"
                  isClearable={true}
                  isSearchable={true}
                  options={getMappedProductsList(productsList)}
                  value={selectedProduct}
                  loadMoreData={handleLoadMoreProducts}
                  loading={fetchingProductsList}
                  onChange={(e) => {
                    if (e) {
                      setSelectedProduct(e)
                    } else {
                      setSelectedProduct(null);
                      setQuantity("");
                      setFilters({ search: '' });
                      dispatch(GetProductsList({
                        sku_id: skus[rowIndex].id,
                        page
                      }));
                    }
                  }}
                  handleSearch={handleProductSearch}
                />
              </div>
            </Col>
            <Col span={13}>
              <div className='d-flex align-items-end gap-3 add-sku-btn'>
                <Input
                  placeholder="Enter Quantity"
                  value={quantity}
                  type="number"
                  onChange={(e) => setQuantity(e.target.value)}
                  label="Pack Quantity"
                  width="70%"
                  marginBottom="0"
                />
                <Button
                  text="Add Product"
                  className="mb-0"
                  minWidth="100px"
                  onClick={handleAddProduct}
                  disabled={!selectedProduct || !quantity || quantity <= 0 || !isAllowedMapProduct}
                />
              </div>
            </Col>
          </Row>
          {addedProducts.length === 0 ?
            <div className='no-data d-flex flex-column align-items-center justify-content-center'>
              <img src={noData} alt='no-data' />
              <h6>No Data</h6>
            </div>
            :
            <div className='map-height'>
              {addedProducts.map((product, index) => (
                <div className="product-title option-product w-100 mb-2" key={index}>
                  <img src={product.image} alt="product" width={28} height={28} className="product-img" />
                  <div className="d-flex justify-content-between gap-3 w-100">
                    <Box sx={{ maxWidth: '650px' }}>
                      <Tooltip placement="bottom" title={product.title}>
                        <span className="product-name">{product.title}</span>
                      </Tooltip>
                      <div className="d-flex gap-2">
                        <div className="asin">
                          <span>{product.identifier_type} <span>{product.identifier_value}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(product.identifier_value)} />
                        </div>
                        <div className="asin qty">
                          <span>Qty: <span>{product.qty}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(product.qty)} />
                        </div>
                      </div>
                    </Box>
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="cursor-pointer"
                      onClick={() => handleDeleteSku(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          }
        </Spin>
      </InventoryWrapper>
    </Modal>
  );
};

export default MapProductModal;
